"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, type MarketDetail } from "@/lib/api";
import { TradePanel } from "./TradePanel";

/**
 * 2026-05-11 — dashboard close-position modal that EMBEDS TradePanel.
 *
 * Founder direction (after a day of close-modal regressions caused by
 * maintaining a parallel ClosePositionModal): close-position is just a
 * SELL on a held outcome. Stop duplicating ~600 lines of orderbook /
 * balance / fee / slippage / region logic — render the canonical
 * TradePanel inside a modal and let it do its job.
 *
 * Layout: bottom-sheet on mobile (full width, slides from bottom,
 * matches Polymarket / Robinhood mobile flow); centered card on
 * desktop. Body scrolls when TradePanel exceeds viewport height.
 *
 * Props:
 *   marketId / marketSlug — used to fetch MarketDetail on mount.
 *   outcomeSortOrder      — 0 (YES) or 1 (NO) — pre-selects the side.
 *   onClose               — dismiss callback; also fires on Esc /
 *                           backdrop / successful order placement
 *                           (TradePanel surfaces success inline; the
 *                           modal closes once the user is done).
 */
interface TradePanelModalProps {
  marketSlug: string;
  outcomeSortOrder: number;
  onClose: () => void;
  // 2026-05-11 — passed from DashboardTabs when the user clicks
  // [Edit] on a pending sell row. Pre-fills the SELL form with the
  // existing pending order's amount + price, so submit becomes a
  // tweak-and-replace instead of typing the values again. The
  // cancel-and-replace flow inside TradePanel already handles
  // the matcher's "open SELL orders + this order would exceed your
  // position" preflight by cancelling pending sells first.
  initialAmount?: string;
  initialPriceCents?: number;
  // 2026-05-12 — when set, the panel enters Edit mode: excludes the
  // order from reservedShares (so "X held" reflects the post-cancel
  // pool) and cancels-then-replaces on submit. See TradePanel's
  // editingOrderId prop docs for the full contract.
  editingOrderId?: string;
}

export function TradePanelModal({
  marketSlug,
  outcomeSortOrder,
  onClose,
  initialAmount,
  initialPriceCents,
  editingOrderId,
}: TradePanelModalProps) {
  const [market, setMarket] = useState<MarketDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getMarket(marketSlug)
      .then((m) => {
        if (!cancelled) setMarket(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error
              ? err.message
              : "Couldn't load market — try refreshing.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [marketSlug]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Close position"
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      {/* Mobile: bottom sheet (full width, rounded top corners, slides up).
          Desktop (sm+): centered card with max-width + rounded corners.
          maxHeight uses dvh so the iOS dynamic toolbar doesn't clip. */}
      <div
        className="relative w-full overflow-y-auto rounded-t-xl border-t border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl sm:max-w-md sm:rounded-xl sm:border"
        style={{
          maxHeight: "calc(100dvh - 1rem)",
        }}
      >
        {/* Mobile grab handle — visual cue that the sheet can be
            dismissed by tapping the backdrop. Hidden on desktop. */}
        <div className="flex justify-center py-2 sm:hidden">
          <div className="h-1 w-12 rounded-full bg-[var(--color-border)]" />
        </div>
        {loadError ? (
          <div className="px-4 py-6 text-center text-[12px] text-[var(--color-no)]">
            {loadError}
          </div>
        ) : !market ? (
          <div className="px-4 py-6 text-center text-[12px] text-[var(--color-text3)]">
            Loading…
          </div>
        ) : (
          <div className="px-3 pb-4 sm:px-4">
            <TradePanel
              market={market}
              initialMode="sell"
              initialSide={outcomeSortOrder === 0 ? "yes" : "no"}
              initialCloseIntent
              initialAmount={initialAmount}
              initialPriceCents={initialPriceCents}
              editingOrderId={editingOrderId}
              compact
              onOrderPlaced={onClose}
            />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
